import { useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { TFunction } from "i18next";
import { ResumeUploadComponent } from "../../../shared/components/ResumeUpload";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { BaseContext } from "../../../shared/base-context";
import { useStableQuery } from "../../../shared/hooks/useStableQuery";
import { Id } from "../../../convex/_generated/dataModel";
import { Input } from "../../../catalyst-components/input";
import { CheckboxGroup, CheckboxField, Checkbox } from "../../../catalyst-components/checkbox";
import { RadioGroup, RadioField, Radio } from "../../../catalyst-components/radio";
import { Label } from "../../../catalyst-components/fieldset";
import pig2 from '../../../assets/graphics/pig2.gif';
import { Button } from "../../../catalyst-components/button";
import { Textarea } from "../../../catalyst-components/textarea";

interface StepProps {
    handleNext: () => void;
}

interface NameStepProps extends StepProps {
    name: string;
}

interface LocationStepProps extends StepProps {
    location: string;
}

const states = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming",
];

// Step metadata: title, subtitle per step — now a function that accepts t() for i18n
const getStepMeta = (t: TFunction): { title: string; subtitle?: string }[] => [
    { title: t('onboarding.step1Title'), subtitle: t('onboarding.step1Subtitle') },
    { title: t('onboarding.step3Title'), subtitle: "" }, // subtitle set dynamically
    { title: t('onboarding.step4Title'), subtitle: "" }, // subtitle set dynamically
    { title: t('onboarding.step5Title'), subtitle: t('onboarding.step5Subtitle') },
    { title: t('onboarding.step6Title'), subtitle: t('onboarding.step6Subtitle') },
    { title: t('onboarding.step7Title'), subtitle: t('onboarding.step7Subtitle') },
    { title: t('onboarding.step8Title'), subtitle: t('onboarding.step8Subtitle') },
    { title: t('onboarding.step10Title'), subtitle: t('onboarding.step10Subtitle') },
    { title: t('onboarding.step9Title'), subtitle: t('onboarding.step9Subtitle') },
    { title: t('onboarding.step11Title'), subtitle: t('onboarding.step11Subtitle') },
    { title: t('onboarding.step12Title'), subtitle: t('onboarding.step12Subtitle') },
];

const Step1 = ({ handleNext }: StepProps) => {
    const [readyToMoveOn, setReadyToMoveOn] = useState(false);

    useEffect(() => {
        if (readyToMoveOn) {
            const timer = setTimeout(() => {
                handleNext();
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [readyToMoveOn, handleNext]);

    return (
        <div className="flex flex-col items-center">
            <ResumeUploadComponent handleResumeUploaded={setReadyToMoveOn} />
        </div>
    );
};

const Step3 = ({ handleNext, name }: NameStepProps) => {
    const { t } = useTranslation();
    const [isNameCorrect, setIsNameCorrect] = useState(true);
    const [nameInput, setNameInput] = useState(name);
    const updateName = useMutation(api.user_metadata.updateName);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveName = async () => {
        await updateName({ user_id: convexUserId as Id<"users">, name: nameInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            {isNameCorrect ? (
                <>
                    <p className="text-3xl text-primary mb-8">{name}?</p>
                    <Button onClick={handleNext} color="primary" className="w-full max-w-md px-8 py-3 rounded-full mb-3">{t('onboarding.thatsMe')}</Button>
                    <Button onClick={() => setIsNameCorrect(false)} color="primaryLight" className="w-full max-w-md px-8 py-3 rounded-full">{t('onboarding.letMeChangeIt')}</Button>
                </>
            ) : (
                <>
                    <Input type="text" value={nameInput} autoFocus={true} onChange={(e) => setNameInput(e.target.value)} className="w-full max-w-md mb-4" />
                    <Button onClick={handleSaveName} disabled={nameInput === ''} color="primary" className="w-full max-w-md px-8 py-3 rounded-full">{t('onboarding.thatsBetter')}</Button>
                </>
            )}
        </div>
    );
};

const Step4 = ({ handleNext, name }: NameStepProps) => {
    const { t } = useTranslation();
    const [nicknameInput, setNicknameInput] = useState(name?.split(' ')[0] || '');
    const updateNickname = useMutation(api.user_metadata.updateNickname);
    const { convexUserId } = useContext(BaseContext);
    const [isNicknameCorrect, setIsNicknameCorrect] = useState(true);

    const handleSaveNickname = async () => {
        await updateNickname({ user_id: convexUserId as Id<"users">, nickname: nicknameInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            {isNicknameCorrect ? (
                <>
                    <p className="text-3xl text-primary mb-8">{nicknameInput}?</p>
                    <Button onClick={handleSaveNickname} color="primary" className="w-full max-w-md px-8 py-3 rounded-full mb-3">{t('onboarding.thatsMe')}</Button>
                    <Button onClick={() => setIsNicknameCorrect(false)} color="primaryLight" className="w-full max-w-md px-8 py-3 rounded-full">{t('onboarding.letMeChangeIt')}</Button>
                </>
            ) : (
                <>
                    <Input type="text" value={nicknameInput} autoFocus={true} onChange={(e) => setNicknameInput(e.target.value)} className="w-full max-w-md mb-4" />
                    <Button onClick={handleSaveNickname} disabled={nicknameInput === ''} color="primary" className="w-full max-w-md px-8 py-3 rounded-full">{t('onboarding.thatsBetter')}</Button>
                </>
            )}
        </div>
    );
};

const Step5 = ({ handleNext, location }: LocationStepProps) => {
    const { t } = useTranslation();
    const [locationInput, setLocationInput] = useState(location);
    const updateState = useMutation(api.user_metadata.updateState);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveLocation = async () => {
        await updateState({ user_id: convexUserId as Id<"users">, state: locationInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            <select
                value={locationInput}
                autoFocus={true}
                onChange={(e) => setLocationInput(e.target.value)}
                className="w-full max-w-md mb-6 p-3 rounded-xl border-2 border-border/50 bg-card text-foreground focus:border-primary focus:outline-none"
            >
                <option value="">{t('onboarding.selectState')}</option>
                {states.map((state) => (
                    <option key={state} value={state}>{state}</option>
                ))}
            </select>
            <Button onClick={handleSaveLocation} disabled={locationInput === ''} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

const Step6 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const [reasonForSearchInput, setReasonForSearchInput] = useState('');
    const updateReasonForSearch = useMutation(api.user_metadata.updateReasonForSearch);
    const { convexUserId } = useContext(BaseContext);

    const reasons = [
        { value: "Laid off", label: t('onboarding.reasonLaidOff') },
        { value: "Let go", label: t('onboarding.reasonLetGo') },
        { value: "Resigned", label: t('onboarding.reasonResigned') },
        { value: "New grad", label: t('onboarding.reasonNewGrad') },
        { value: "Emerging from career break", label: t('onboarding.reasonCareerBreak') },
        { value: "Under-employed (gigging or working part-time)", label: t('onboarding.reasonUnderemployed') },
        { value: "Employed (but looking to leave)", label: t('onboarding.reasonEmployedLooking') },
    ];

    const handleSaveReasonForSearch = async () => {
        await updateReasonForSearch({ user_id: convexUserId as Id<"users">, reason_for_search: reasonForSearchInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            <select
                value={reasonForSearchInput}
                autoFocus={true}
                onChange={(e) => setReasonForSearchInput(e.target.value)}
                className="w-full max-w-md mb-6 p-3 rounded-xl border-2 border-border/50 bg-card text-foreground focus:border-primary focus:outline-none"
            >
                <option value="">{t('onboarding.selectReason')}</option>
                {reasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                ))}
            </select>
            <Button onClick={handleSaveReasonForSearch} disabled={reasonForSearchInput === ''} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

const Step7 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const [irlSearchPhaseInput, setIrlSearchPhaseInput] = useState<string[]>([]);
    const updateIrlSearchPhase = useMutation(api.user_metadata.updateIrlSearchPhase);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveIrlSearchPhase = async () => {
        await updateIrlSearchPhase({ user_id: convexUserId as Id<"users">, irl_search_phase: irlSearchPhaseInput.join(', ') });
        handleNext();
    };

    const handleToggleOption = (value: string) => {
        setIrlSearchPhaseInput(prev =>
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    };

    const options = [
        { value: "Calibrating", label: t('onboarding.focusCalibrating'), description: t('onboarding.focusCalibratingDesc') },
        { value: "Optimizing & Branding", label: t('onboarding.focusOptimizing'), description: t('onboarding.focusOptimizingDesc') },
        { value: "Applying & Networking", label: t('onboarding.focusApplying'), description: t('onboarding.focusApplyingDesc') },
        { value: "Interviewing", label: t('onboarding.focusInterviewing'), description: t('onboarding.focusInterviewingDesc') },
        { value: "Accepting & Acclimating", label: t('onboarding.focusAccepting'), description: t('onboarding.focusAcceptingDesc') },
    ];

    return (
        <div className="flex flex-col items-center w-full">
            <div className="w-full max-w-md space-y-3 mb-6">
                {options.map((option) => (
                    <button
                        key={option.value}
                        onClick={() => handleToggleOption(option.value)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                            irlSearchPhaseInput.includes(option.value)
                                ? 'border-primary bg-primary/5'
                                : 'border-border/50 bg-card'
                        }`}
                    >
                        <div className="flex items-start gap-3">
                            <div className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 ${
                                irlSearchPhaseInput.includes(option.value)
                                    ? 'bg-primary border-primary text-white'
                                    : 'border-border bg-card'
                            }`}>
                                {irlSearchPhaseInput.includes(option.value) && (
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                            <div>
                                <p className="font-semibold text-foreground">{option.label}</p>
                                <p className="text-sm text-muted-foreground">{option.description}</p>
                            </div>
                        </div>
                    </button>
                ))}
            </div>
            <Button onClick={handleSaveIrlSearchPhase} disabled={irlSearchPhaseInput.length === 0} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

const Step8 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const [searchDurationInput, setSearchDurationInput] = useState('');
    const updateSearchDuration = useMutation(api.user_metadata.updateSearchDuration);
    const { convexUserId } = useContext(BaseContext);

    const options = [
        { value: "Under a month", label: t('onboarding.durationUnderMonth') },
        { value: "~ 3 months", label: t('onboarding.duration3Months') },
        { value: "~ 6 months", label: t('onboarding.duration6Months') },
        { value: "~ 9 months", label: t('onboarding.duration9Months') },
        { value: "Over 1 year", label: t('onboarding.durationOverYear') },
    ];

    const handleSaveSearchDuration = async () => {
        await updateSearchDuration({ user_id: convexUserId as Id<"users">, search_duration: searchDurationInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            <select
                value={searchDurationInput}
                autoFocus={true}
                onChange={(e) => setSearchDurationInput(e.target.value)}
                className="w-full max-w-md mb-6 p-3 rounded-xl border-2 border-border/50 bg-card text-foreground focus:border-primary focus:outline-none"
            >
                <option value="">{t('onboarding.selectDuration')}</option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <Button onClick={handleSaveSearchDuration} disabled={searchDurationInput === ''} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

export const Step9 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const [dislikedAspectsOfSearchInput, setDislikedAspectsOfSearchInput] = useState('');
    const updateDislikedAspectsOfSearch = useMutation(api.user_metadata.updateDislikedAspectsOfSearch);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveDislikedAspectsOfSearch = async () => {
        await updateDislikedAspectsOfSearch({ user_id: convexUserId as Id<"users">, disliked_aspects_of_search: dislikedAspectsOfSearchInput.trim() });
        handleNext();
    };

    return (
        <div className="flex flex-col items-center w-full">
            <Textarea value={dislikedAspectsOfSearchInput} autoFocus={true} onChange={(e) => setDislikedAspectsOfSearchInput(e.target.value)} className="w-full max-w-md mb-6" />
            <Button onClick={handleSaveDislikedAspectsOfSearch} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

const Step10 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const options = [1,2,3,4,5,6,7,8,9,10];
    const [howReadyToStartSearchInput, setHowReadyToStartSearchInput] = useState('');
    const updateHowReadyToStartSearch = useMutation(api.user_metadata.updateHowReadyToStartSearch);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveHowReadyToStartSearch = async () => {
        await updateHowReadyToStartSearch({ user_id: convexUserId as Id<"users">, how_ready_to_start_search: howReadyToStartSearchInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex items-center justify-between w-full max-w-md mb-2">
                <span className="text-sm text-muted-foreground">{t('onboarding.notReady')}</span>
                <span className="text-sm text-muted-foreground">{t('onboarding.veryReady')}</span>
            </div>
            <div className="flex gap-2 mb-6 flex-wrap justify-center">
                {options.map((option) => (
                    <button
                        key={option}
                        onClick={() => setHowReadyToStartSearchInput(option.toString())}
                        className={`w-11 h-11 rounded-xl border-2 font-semibold transition-all ${
                            howReadyToStartSearchInput === option.toString()
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border/50 bg-card text-foreground'
                        }`}
                    >
                        {option}
                    </button>
                ))}
            </div>
            <Button onClick={handleSaveHowReadyToStartSearch} disabled={howReadyToStartSearchInput === ''} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
};

const Step11 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const [howDidYouHearAboutUsInput, setHowDidYouHearAboutUsInput] = useState('');
    const updateHowDidYouHearAboutUs = useMutation(api.user_metadata.updateHowDidYouHearAboutUs);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveHowDidYouHearAboutUs = async () => {
        await updateHowDidYouHearAboutUs({ user_id: convexUserId as Id<"users">, how_did_you_hear_about_us: howDidYouHearAboutUsInput });
        handleNext();
    };
    return (
        <div className="flex flex-col items-center w-full">
            <Input type="text" value={howDidYouHearAboutUsInput} autoFocus={true} onChange={(e) => setHowDidYouHearAboutUsInput(e.target.value)} className="w-full max-w-md mb-6" />
            <Button onClick={handleSaveHowDidYouHearAboutUs} color="primary" className="px-8 py-3 rounded-full">{t('common.continue')}</Button>
        </div>
    );
}

const Step12 = ({ handleNext }: StepProps) => {
    const { t } = useTranslation();
    const setOnboardingCompleted = useMutation(api.user_metadata.setOnboardingCompleted);
    const { convexUserId } = useContext(BaseContext);

    const handleSaveOnboardingCompleted = async () => {
        await setOnboardingCompleted({ user_id: convexUserId as Id<"users"> });
        handleNext();
    };

    return (
        <div className="flex flex-col items-center">
            <img src={pig2} alt="Gruntled mascot" className="w-96 object-contain mb-6" />
            <Button onClick={handleSaveOnboardingCompleted} color="primary" className="px-8 py-3 rounded-full">{t('onboarding.getGruntled')}</Button>
        </div>
    );
};

const TOTAL_STEPS = 11;

export const OnboardingContent = () => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const [prevStep, setPrevStep] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const { convexUserId } = useContext(BaseContext);
    const metadata = useStableQuery(api.user_metadata.getUserMetadata, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
    const onboardingStepIndex = useStableQuery(api.user_metadata.getOnboardingStepIndex, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
    const setOnboardingStepIndex = useMutation(api.user_metadata.setOnboardingStepIndex);

    const stepMeta = getStepMeta(t);

    useEffect(() => {
        setCurrentStep(onboardingStepIndex || 0);
    }, [onboardingStepIndex]);

    const handleNext = () => {
        if (!isAnimating && currentStep < TOTAL_STEPS) {
            const nextStep = currentStep + 1;
            setPrevStep(currentStep);
            setIsAnimating(true);
            setCurrentStep(nextStep);
            setTimeout(() => {
                setIsAnimating(false);
            }, 500);
            setOnboardingStepIndex({ user_id: convexUserId as Id<"users">, onboarding_step_index: nextStep });
        }
    };

    const handleBack = () => {
        if (!isAnimating && currentStep > 0) {
            const prevStepIdx = currentStep - 1;
            setPrevStep(currentStep);
            setIsAnimating(true);
            setCurrentStep(prevStepIdx);
            setTimeout(() => {
                setIsAnimating(false);
            }, 500);
            setOnboardingStepIndex({ user_id: convexUserId as Id<"users">, onboarding_step_index: prevStepIdx });
        }
    };

    const renderStep = (step: number) => {
        switch (step) {
            case 0:
                return <Step1 handleNext={handleNext} />;
            case 1:
                return <Step3 handleNext={handleNext} name={metadata?.legal_name} />;
            case 2:
                return <Step4 handleNext={handleNext} name={metadata?.nickname || metadata?.legal_name} />;
            case 3:
                return <Step5 handleNext={handleNext} location={metadata?.state} />;
            case 4:
                return <Step6 handleNext={handleNext} />;
            case 5:
                return <Step7 handleNext={handleNext} />;
            case 6:
                return <Step8 handleNext={handleNext} />;
            case 7:
                return <Step10 handleNext={handleNext} />;
            case 8:
                return <Step9 handleNext={handleNext} />;
            case 9:
                return <Step11 handleNext={handleNext} />;
            case 10:
                return <Step12 handleNext={handleNext} />;
            default:
                return null;
        }
    };

    // Dynamic title/subtitle for name-related steps
    const getTitle = () => {
        if (currentStep === 1) return t('onboarding.step3Title');
        if (currentStep === 2) return t('onboarding.step4Title');
        return stepMeta[currentStep]?.title || "";
    };

    const getSubtitle = () => {
        if (currentStep === 1) return undefined; // subtitle handled inline by Step3
        if (currentStep === 2) return undefined; // subtitle handled inline by Step4
        return stepMeta[currentStep]?.subtitle;
    };

    return (
        <section className="fixed inset-0 z-20 bg-background animate-fade-in flex flex-col overflow-hidden">
            {/* Title — fixed position, consistent across steps */}
            <div className="text-center px-6 mt-16 mb-2 max-w-2xl mx-auto w-full">
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{getTitle()}</h1>
                {getSubtitle() && (
                    <p className="text-lg text-muted-foreground mt-3">{getSubtitle()}</p>
                )}
            </div>

            {/* Step content — animated, fills remaining space */}
            <div className="flex-1 overflow-y-auto relative px-6 mt-8">
                <div className="max-w-2xl mx-auto w-full">
                    {isAnimating ? (
                        <>
                            <div className="absolute inset-0 px-6 onboarding-slide-out">
                                <div className="max-w-2xl mx-auto w-full">
                                    {renderStep(prevStep)}
                                </div>
                            </div>
                            <div className="onboarding-slide-in">
                                {renderStep(currentStep)}
                            </div>
                        </>
                    ) : (
                        renderStep(currentStep)
                    )}
                </div>
            </div>

            {/* Progress dots + Go back */}
            <div className="flex flex-col items-center gap-3 pb-8 pt-4">
                <div className="flex justify-center gap-2">
                    {Array.from({ length: TOTAL_STEPS }).map((_, idx) => (
                        <div
                            key={idx}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                idx === currentStep
                                    ? 'w-8 bg-primary'
                                    : idx < currentStep
                                      ? 'w-2 bg-primary/40'
                                      : 'w-2 bg-border'
                            }`}
                        />
                    ))}
                </div>
                {currentStep > 0 && (
                    <button
                        onClick={handleBack}
                        className="text-muted-foreground hover:text-foreground text-base font-medium underline transition-colors"
                    >
                        {t('onboarding.goBack')}
                    </button>
                )}
            </div>

            <style>{`
                @keyframes onboarding-slide-out {
                    from { opacity: 1; transform: translateY(0); }
                    to { opacity: 0; transform: translateY(100%); }
                }
                @keyframes onboarding-slide-in {
                    from { opacity: 0; transform: translateY(-50%); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .onboarding-slide-out {
                    animation: onboarding-slide-out 0.3s ease-in-out forwards;
                }
                .onboarding-slide-in {
                    animation: onboarding-slide-in 0.3s ease-in-out forwards;
                }
            `}</style>
        </section>
    );
};
